import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { intakeApi } from '../api/services';
import toast from 'react-hot-toast';
import { Upload, Inbox, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';

export default function IntakeBatches() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const res = await intakeApi.list();
      setBatches(res.data.batches || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load batches');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Intake batches</h1>
          <p className="text-sm text-gray-500 mt-1">MBO uploads — each batch contains orders that arrived together.</p>
        </div>
        <Link to="/intake/upload"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
          <Upload className="w-4 h-4" /> Upload MBO
        </Link>
      </div>

      {loading && <div className="text-sm text-gray-500">Loading…</div>}

      {!loading && batches.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <Inbox className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600">No intake batches yet.</p>
          <Link to="/intake/upload" className="text-blue-600 hover:underline text-sm mt-2 inline-block">
            Upload your first MBO file
          </Link>
        </div>
      )}

      {!loading && batches.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-4 py-3">Batch number</th>
                <th className="text-left px-4 py-3">Source</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Uploaded by</th>
                <th className="text-right px-4 py-3">Orders</th>
                <th className="text-left px-4 py-3">Issues</th>
                <th className="text-left px-4 py-3">Uploaded at</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => {
                const failedCount = Array.isArray(b.failed_rows) ? b.failed_rows.length : 0;
                return (
                  <tr key={b.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-blue-600">
                      <Link to={`/intake/${b.id}`}>{b.batch_number}</Link>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {b.source_type}{b.source_filename ? ` · ${b.source_filename}` : ''}
                    </td>
                    <td className="px-4 py-3 capitalize">{b.status}</td>
                    <td className="px-4 py-3 text-gray-600">{b.uploaded_by_name || '—'}</td>
                    <td className="px-4 py-3 text-right">{b.total_orders ?? 0}</td>
                    <td className="px-4 py-3">
                      {failedCount > 0 && (
                        <span className="inline-flex items-center gap-1 text-amber-700 text-xs">
                          <AlertTriangle className="w-3 h-3" />
                          {failedCount} failed row(s)
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {b.created_at ? format(new Date(b.created_at), 'dd MMM yyyy HH:mm') : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
